import { useState, useRef, useEffect, useCallback } from "react";
import {
  Upload,
  Download,
  ArrowLeft,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { QuizQuestion } from "@/types/quiz";

type ImportStep = "upload" | "preview";

const REQUIRED_HEADERS = [
  "Frage-Key",
  "Fragetext",
  "Fragetyp",
  "Option-Text",
  "Option-Wert",
];

const QUESTION_KEY_REGEX = /^[a-z][a-z0-9_]*$/;

const TYPE_MAP: Record<string, QuizQuestion["questionType"]> = {
  "single-choice": "single-choice",
  "multiple-choice": "multiple-choice",
  texteingabe: "text-input",
  schieberegler: "range-slider",
};

const TYPE_LABELS: Record<QuizQuestion["questionType"], string> = {
  "single-choice": "Single-Choice",
  "multiple-choice": "Multiple-Choice",
  "text-input": "Texteingabe",
  "range-slider": "Schieberegler",
};

interface ParsedQuestion {
  question: QuizQuestion;
  isCollision: boolean;
}

interface ParseResult {
  questions: ParsedQuestion[];
  errors: string[];
  totalOptions: number;
}

function decodeCSVBuffer(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return new TextDecoder("utf-8").decode(bytes.slice(3));
  }
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder("windows-1252").decode(bytes);
  }
}

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ";") {
      result.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  result.push(current);
  return result;
}

function parseQuizCSV(text: string, existingKeys: string[]): ParseResult {
  const errors: string[] = [];
  const lines = text.split(/\r?\n/).filter((l) => l.trim());

  if (lines.length < 2) {
    return {
      questions: [],
      errors: [
        "Die Datei muss mindestens eine Kopfzeile und eine Datenzeile enthalten.",
      ],
      totalOptions: 0,
    };
  }

  const headers = parseCSVLine(lines[0]).map((h) => h.trim());
  const missing = REQUIRED_HEADERS.filter((h) => !headers.includes(h));
  if (missing.length > 0) {
    return {
      questions: [],
      errors: [`Fehlende Spalten: ${missing.join(", ")}`],
      totalOptions: 0,
    };
  }

  const idx = {
    key: headers.indexOf("Frage-Key"),
    text: headers.indexOf("Fragetext"),
    type: headers.indexOf("Fragetyp"),
    optText: headers.indexOf("Option-Text"),
    optVal: headers.indexOf("Option-Wert"),
  };

  const questionMap = new Map<
    string,
    { text: string; type: string; options: { text: string; value: string }[] }
  >();
  const keyOrder: string[] = [];
  let totalOptions = 0;

  for (let i = 1; i < lines.length; i++) {
    const cols = parseCSVLine(lines[i]);
    const row = i + 1;
    const key = (cols[idx.key] ?? "").trim().toLowerCase();
    const qText = (cols[idx.text] ?? "").trim();
    const qType = (cols[idx.type] ?? "").trim();
    const optText = (cols[idx.optText] ?? "").trim();
    const optVal = (cols[idx.optVal] ?? "").trim();

    if (!key) {
      errors.push(`Zeile ${row}: Frage-Key fehlt.`);
      continue;
    }
    if (!QUESTION_KEY_REGEX.test(key)) {
      errors.push(
        `Zeile ${row}: Frage-Key "${key}" ist ungueltig (nur Kleinbuchstaben, Ziffern, Unterstriche).`,
      );
      continue;
    }
    if (!qText) {
      errors.push(`Zeile ${row}: Fragetext fehlt.`);
      continue;
    }
    if (!TYPE_MAP[qType.toLowerCase()]) {
      errors.push(`Zeile ${row}: Unbekannter Fragetyp "${qType}".`);
      continue;
    }
    if (!optText) {
      errors.push(`Zeile ${row}: Option-Text fehlt.`);
      continue;
    }
    if (!optVal) {
      errors.push(`Zeile ${row}: Option-Wert fehlt.`);
      continue;
    }

    if (!questionMap.has(key)) {
      questionMap.set(key, { text: qText, type: qType, options: [] });
      keyOrder.push(key);
    }
    questionMap.get(key)!.options.push({ text: optText, value: optVal });
    totalOptions++;
  }

  const questions: ParsedQuestion[] = [];
  let order = 1;
  for (const key of keyOrder) {
    const q = questionMap.get(key)!;
    if (q.options.length < 2) {
      errors.push(
        `Frage "${key}": Mindestens 2 Antwortoptionen erforderlich (${q.options.length} gefunden).`,
      );
      continue;
    }
    questions.push({
      question: {
        questionKey: key,
        questionText: q.text,
        questionType: TYPE_MAP[q.type.toLowerCase()] ?? "single-choice",
        displayOrder: order++,
        isOptional: false,
        options: q.options.map((o) => ({
          id: crypto.randomUUID(),
          text: o.text,
          value: o.value,
        })),
      },
      isCollision: existingKeys.includes(key),
    });
  }

  return { questions, errors, totalOptions };
}

function downloadTemplate() {
  const BOM = "\uFEFF";
  const header = "Frage-Key;Fragetext;Fragetyp;Option-Text;Option-Wert";
  const rows = [
    "hauttyp;Wie ist dein Hauttyp?;Single-Choice;Trocken;hauttyp-trocken",
    "hauttyp;Wie ist dein Hauttyp?;Single-Choice;Fettig;hauttyp-fettig",
    "hauttyp;Wie ist dein Hauttyp?;Single-Choice;Mischhaut;hauttyp-misch",
    "budget;Was moechtest du ausgeben?;Single-Choice;Unter 20\u20ac;budget-niedrig",
    "budget;Was moechtest du ausgeben?;Single-Choice;20\u201350\u20ac;budget-mittel",
    "budget;Was moechtest du ausgeben?;Single-Choice;\u00dcber 50\u20ac;budget-premium",
  ];
  const csv = `${BOM}${header}\n${rows.join("\n")}\n`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "quiznudge-fragen-vorlage.csv";
  a.click();
  URL.revokeObjectURL(url);
}

interface QuizImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (questions: QuizQuestion[]) => void;
  existingKeys: string[];
  isSaving?: boolean;
}

export function QuizImportDialog({
  open,
  onClose,
  onImport,
  existingKeys,
  isSaving = false,
}: QuizImportDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<ImportStep>("upload");
  const [isDragOver, setIsDragOver] = useState(false);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep("upload");
    setParseResult(null);
    setFileError(null);
    setIsDragOver(false);
  }, [open]);

  const handleFile = useCallback(
    async (file: File) => {
      setFileError(null);
      setParseResult(null);

      if (
        file.type !== "text/csv" &&
        file.type !== "application/vnd.ms-excel" &&
        !file.name.endsWith(".csv")
      ) {
        setFileError("Bitte eine CSV-Datei auswählen.");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setFileError("Die Datei ist zu groß. Maximal 5 MB erlaubt.");
        return;
      }

      try {
        const buffer = await file.arrayBuffer();
        const text = decodeCSVBuffer(buffer);
        const result = parseQuizCSV(text, existingKeys);
        setParseResult(result);
        if (result.questions.length > 0) {
          setStep("preview");
        }
      } catch {
        setFileError("Die Datei konnte nicht gelesen werden.");
      }
    },
    [existingKeys],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      e.target.value = "";
    },
    [handleFile],
  );

  const handleImport = useCallback(() => {
    if (!parseResult) return;
    onImport(parseResult.questions.map((pq) => pq.question));
  }, [parseResult, onImport]);

  if (!open) return null;

  const collisionCount =
    parseResult?.questions.filter((q) => q.isCollision).length ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
        }}
        role="button"
        tabIndex={-1}
        aria-label="Dialog schließen"
      />
      <div className="relative z-50 mx-4 max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg border bg-background p-6 shadow-lg">
        {step === "upload" && (
          <>
            <h2 className="text-lg font-semibold">
              Fragen aus CSV importieren
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Lade eine CSV-Datei mit deinen Quiz-Fragen hoch. Jede Zeile ist
              eine Antwortoption.
            </p>

            <div className="mt-4">
              <Button variant="outline" size="sm" onClick={downloadTemplate}>
                <Download className="mr-2 h-4 w-4" />
                Vorlage herunterladen (.csv)
              </Button>
            </div>

            <div
              role="button"
              tabIndex={0}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              className={cn(
                "mt-4 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors",
                isDragOver
                  ? "border-primary bg-primary/5"
                  : "border-muted-foreground/25 hover:border-muted-foreground/50",
              )}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileChange}
              />
              <Upload className="h-8 w-8 text-muted-foreground" />
              <p className="mt-3 text-sm font-medium">
                CSV-Datei hierher ziehen oder klicken zum Auswählen
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Semikolon-getrennt, UTF-8 oder Windows-1252
              </p>
            </div>

            {fileError && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <p className="text-sm text-destructive">{fileError}</p>
              </div>
            )}

            {parseResult &&
              parseResult.errors.length > 0 &&
              parseResult.questions.length === 0 && (
                <div className="mt-3 space-y-1 rounded-lg border border-destructive/50 bg-destructive/10 p-3">
                  {parseResult.errors.map((err, i) => (
                    <p key={i} className="text-sm text-destructive">
                      {err}
                    </p>
                  ))}
                </div>
              )}

            <div className="mt-6 flex justify-end">
              <Button variant="outline" onClick={onClose}>
                Abbrechen
              </Button>
            </div>
          </>
        )}

        {step === "preview" && parseResult && (
          <>
            <h2 className="text-lg font-semibold">Vorschau</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {parseResult.questions.length} Fragen mit{" "}
              {parseResult.totalOptions} Antwortoptionen erkannt
            </p>

            {collisionCount > 0 && (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm text-amber-800">
                  {collisionCount}{" "}
                  {collisionCount === 1
                    ? "Frage existiert bereits und wird aktualisiert."
                    : "Fragen existieren bereits und werden aktualisiert."}
                </p>
              </div>
            )}

            {parseResult.errors.length > 0 && (
              <div className="mt-3 space-y-1 rounded-lg border border-destructive/50 bg-destructive/10 p-3">
                {parseResult.errors.map((err, i) => (
                  <p key={i} className="text-sm text-destructive">
                    {err}
                  </p>
                ))}
              </div>
            )}

            <div className="mt-4 space-y-3">
              {parseResult.questions.map(({ question: q, isCollision }) => (
                <div
                  key={q.questionKey}
                  className={cn(
                    "rounded-lg border p-4",
                    isCollision && "border-amber-300 bg-amber-50/50",
                  )}
                >
                  <div className="min-w-0">
                    <p className="font-medium">
                      {q.displayOrder}. {q.questionText}
                      {isCollision && (
                        <span className="ml-2 text-xs text-amber-600">
                          (wird aktualisiert)
                        </span>
                      )}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {TYPE_LABELS[q.questionType]} &middot;{" "}
                      {q.options?.length ?? 0} Optionen
                      <span className="ml-2 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">
                        {q.questionKey}
                      </span>
                    </p>
                    {q.options && q.options.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {q.options.map((opt) => (
                          <span
                            key={opt.id}
                            className="inline-flex items-center rounded bg-muted px-2 py-0.5 text-xs"
                          >
                            {opt.text}
                            <span className="ml-1 text-muted-foreground">
                              ({opt.value})
                            </span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-between">
              <Button
                variant="outline"
                onClick={() => {
                  setStep("upload");
                  setParseResult(null);
                }}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Zurück
              </Button>
              <Button
                onClick={handleImport}
                disabled={parseResult.questions.length === 0 || isSaving}
              >
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isSaving ? "Wird gespeichert..." : "Fragen übernehmen"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
